import React, { Component } from 'react';
import SlidePanel from '../shared/SlidePanel';
import { bookingService } from '../../_services/booking.service';
import { leaveService } from '../../_services/leave.service';
import { eventService } from '../../_services/event.service';
import AffectedBookings from './repeat/AffectedBookings';
import { affectedFrom, affectedUntil, previewFor, previewKey, savedWords } from './repeat/affected';
import toast from '../shared/Toast';
import { matchEventEmoji, COLOUR_PRESETS } from './eventIllustrations';
import ShiftTimePicker from '../Diary/ShiftTimePicker';
import InlinePickerSelect from '../shared/InlinePickerSelect';
import StaffPicker from '../Diary/StaffPicker';
import DiaryDatePicker from '../Diary/DiaryDatePicker';
import moment from 'moment';
import './NewBookingPanel.css';

/**
 * NewEventPanel - Slide-out for creating Events or Leave from the diary.
 * Props: mode ('event' | 'leave'), staffId, bookingDate, startMinutes, staffList, onClose, onCreated, dispatch,
 * onOpenBooking(booking, 'edit' | 'message') for the bookings it lands on, bookingsVersion (bumped when one moves)
 */
class NewEventPanel extends Component {
  constructor(props) {
    super(props);
    this._dateAnchor = null;
    this._endDateAnchor = null;

    const isLeave = props.mode === 'leave';

    this.state = {
      staffId: props.staffId || '',
      resourceId: '',   // set = this blocks a ROOM, not a person
      startDate: props.bookingDate || '',
      endDate: props.endDate || props.bookingDate || '',
      startMinutes: props.startMinutes != null ? props.startMinutes : null,
      // Bailey's proposals arrive with an end, a title and (for leave) a
      // last day and a kind. Everything stays editable before saving.
      endMinutes: props.endMinutes != null ? props.endMinutes
        : (props.startMinutes != null ? props.startMinutes + 60 : null),
      title: props.title || '',
      colour: isLeave ? '#E85D75' : '#29282D',
      emoji: '', // auto-suggested, user can change or clear
      isBookingAllowed: props.isBookingAllowed === true,
      saving: false,
      datePickerOpen: false,
      endDatePickerOpen: false,
      isLeave,
      // Categorises the leave for reporting. 'holiday' is the safe default
      // since it's by far the most common case. Backend stores the value
      // verbatim; reports will filter by it.
      leaveType: props.leaveType || 'holiday',
      // The bookings this would land on (a read-only check as the choices
      // change): null until checked or when it couldn't be
      affected: null,
      affectedUntil: null,
      affectedKey: '',
      affectedChecking: false,
    };
  }

  componentDidMount() {
    this.checkAffected();
  }

  componentDidUpdate(prevProps, prevState) {
    const now = previewKey(this.preview(this.state));
    // A booking was moved from the list: check again
    if (prevProps.bookingsVersion !== this.props.bookingsVersion) this.checkAffected(true);
    else if (now !== previewKey(this.preview(prevState))) this.checkAffected();
  }

  componentWillUnmount() {
    clearTimeout(this._affectedTimer);
    this._unmounted = true;
  }

  preview = st => previewFor(st)

  // Ask which bookings it lands on, a moment after the choices settle.
  // Read-only, and never in the way: if it fails, saving carries on.
  checkAffected = (now = false) => {
    clearTimeout(this._affectedTimer);
    const p = this.preview(this.state);
    if (!p) {
      if (this.state.affected || this.state.affectedKey) this.setState({ affected: null, affectedUntil: null, affectedKey: '', affectedChecking: false });
      return;
    }
    this._affectedTimer = setTimeout(() => { this.runAffected(); }, now ? 0 : 400);
  }

  runAffected = () => {
    const p = this.preview(this.state);
    if (!p) return Promise.resolve(null);
    const key = previewKey(p);
    const seq = (this._affectedSeq || 0) + 1;
    this._affectedSeq = seq;
    this.setState({ affectedChecking: true });
    const call = p.kind === 'leave' ? leaveService.affectedBookings(p.params) : eventService.affectedBookings(p.params);
    return call
      .then((res) => {
        if (this._unmounted || seq !== this._affectedSeq) return null;
        const list = affectedFrom(res);
        this.setState({ affected: list, affectedUntil: affectedUntil(res), affectedKey: key, affectedChecking: false });
        return list;
      })
      .catch(() => {
        if (this._unmounted || seq !== this._affectedSeq) return null;
        this.setState({ affected: null, affectedUntil: null, affectedKey: key, affectedChecking: false });
        return null;
      });
  }

  // Saved. When the list was shown first it's said already; otherwise the
  // toast says what it landed on (null: it couldn't check, so nothing)
  toastSaved = (what, landed) => {
    const shown = this.state.affected && this.state.affected.length;
    if (shown || !(landed && landed.length)) toast.success(what);
    else toast.success(savedWords(what, landed), 6000);
  }

  // Save: the check first, if it hasn't answered for these choices yet.
  // When it finds bookings they're shown and the button becomes Save
  // anyway; a second tap saves.
  handleSaveClick = () => {
    const p = this.preview(this.state);
    if (!p || this.state.affectedKey === previewKey(p)) { this.handleSave(); return; }
    clearTimeout(this._affectedTimer);
    this.setState({ saving: true });
    this.runAffected().then((list) => {
      if (this._unmounted) return;
      this.setState({ saving: false });
      if (list && list.length) {
        if (this._affectedRef && this._affectedRef.scrollIntoView) this._affectedRef.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        return;
      }
      this.handleSave();
    });
  }

  handleSave = () => {
    const { staffId, resourceId, startDate, endDate, startMinutes, endMinutes, title, isBookingAllowed, isLeave } = this.state;

    if (!staffId && !resourceId) {
      toast.error('Please select a staff member');
      return;
    }

    if (isLeave) {
      if (!startDate || !endDate) {
        toast.error('Please select start and end dates');
        return;
      }
      this.setState({ saving: true });
      leaveService.createLeave({
        hairdresserId: staffId,
        startDate: moment(startDate).format('YYYY-MM-DD'),
        endDate: moment(endDate).format('YYYY-MM-DD'),
        title: title || '',
        diaryColour: this.state.colour,
        emoji: this.state.emoji || null,
        // leaveType: 'holiday' | 'sick' | 'other' — persisted on the
        // backend (Joi-validated). Defaults to 'holiday' to preserve the
        // most common case if the FE state ever slips.
        leaveType: this.state.leaveType || 'holiday',
      })
        .then(response => {
          this.setState({ saving: false });
          if (response.data?.errorCode) {
            toast.error(response.data?.messages || 'Could not create leave');
            return;
          }
          this.toastSaved('Leave created', affectedFrom(response));
          if (this.props.onCreated) this.props.onCreated({ kind: 'holiday', staffId, date: moment(startDate).format('YYYY-MM-DD') });
        })
        .catch(() => {
          this.setState({ saving: false });
          toast.error('Something went wrong');
        });
    } else {
      if (!title.trim()) {
        toast.error('Please enter an event title');
        return;
      }
      if (endMinutes <= startMinutes) {
        toast.error('End time must be after start time');
        return;
      }
      this.setState({ saving: true });
      bookingService.createEvent({
        // A room block still rides the creator's event path — the target is
        // the room (resourceId); the creator is just the author.
        userId: staffId || localStorage.getItem('userId'),
        ...(resourceId ? { resourceId } : {}),
        title: title,
        date: moment(startDate).format('YYYY-MM-DD'),
        startTime: startMinutes,
        endTime: endMinutes,
        isBookingAllowed: isBookingAllowed ? true : false,
        diaryColour: this.state.colour,
        emoji: this.state.emoji || null,
        repeatType: 'doesNotRepeat',
      })
        .then(response => {
          this.setState({ saving: false });
          // The API wraps failures as { statusCode: 0, error: { errorCode, message } }
          // on an HTTP 200 — reading errorCode off the top level meant every
          // refused event still toasted "Event created".
          const data = response.data || {};
          if (data.statusCode !== 1 || data.errorCode || data.error) {
            toast.error(data.error?.message || data.error?.responseMessage || data.messages || 'Could not create event');
            return;
          }
          this.toastSaved('Event created', isBookingAllowed ? null : affectedFrom(response));
          if (this.props.onCreated) this.props.onCreated({ kind: 'event', staffId: staffId || resourceId, date: moment(startDate).format('YYYY-MM-DD'), startMinutes });
        })
        .catch(() => {
          this.setState({ saving: false });
          toast.error('Something went wrong');
        });
    }
  }

  render() {
    const { staffList, onClose } = this.props;
    const { staffId, startDate, endDate, startMinutes, endMinutes, title, isBookingAllowed, saving, isLeave, datePickerOpen, endDatePickerOpen } = this.state;

    return (
      <SlidePanel onClose={onClose} className="bdp-panel nbp-panel" width={420}>
        {/* Header */}
        <div className="bdp-header">
          <div className="bdp-header-left">
            <div className="nbp-header-icon" style={{ background: isLeave ? '#e67e22' : '#29282D' }}>
              {isLeave ? (
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 2C5.13 2 2 5.13 2 9C2 12.87 5.13 16 9 16C12.87 16 16 12.87 16 9C16 5.13 12.87 2 9 2Z" stroke="#fff" strokeWidth="1.3"/>
                  <path d="M9 5V9L12 10.5" stroke="#fff" strokeWidth="1.3" strokeLinecap="round"/>
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <rect x="2" y="3" width="14" height="13" rx="2" stroke="#fff" strokeWidth="1.3"/>
                  <path d="M2 7H16" stroke="#fff" strokeWidth="1.3"/>
                  <path d="M6 1V4M12 1V4" stroke="#fff" strokeWidth="1.3" strokeLinecap="round"/>
                </svg>
              )}
            </div>
            <div className="bdp-header-info">
              <h3 className="bdp-client-name">{isLeave ? 'New Leave' : 'New Event'}</h3>
            </div>
          </div>
          <button className="bdp-close-btn" onClick={onClose}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="bdp-body">
          {/* Where: a person's diary, or a room (docs/resources.md §3). A room
              block defaults to ALL DAY — "the room is out" — and the times
              below stay editable to narrow it. */}
          {!isLeave && (this.props.resourceUnits || []).length > 0 && (
            <div className="bdp-edit-field">
              <label className="bdp-edit-label">Where</label>
              <InlinePickerSelect
                options={[{ value: '', label: "A team member's diary" }].concat(
                  (this.props.resourceUnits || []).map(u => ({ value: String(u._id), label: u.name })))}
                value={this.state.resourceId
                  ? { value: this.state.resourceId, label: ((this.props.resourceUnits || []).find(u => String(u._id) === this.state.resourceId) || {}).name || 'Room' }
                  : { value: '', label: "A team member's diary" }}
                onChange={(sel) => {
                  const rid = sel && sel.value ? sel.value : '';
                  this.setState(rid
                    ? { resourceId: rid, startMinutes: 0, endMinutes: 1440 }
                    : { resourceId: '' });
                }}
              />
              {this.state.resourceId && (
                <p className="nep-room-hint">
                  Blocks this room all day — adjust the times below for part of
                  the day. New bookings can't use it; existing ones keep it.
                  The stylists stay free.
                </p>
              )}
            </div>
          )}

          {/* Staff Member */}
          {!this.state.resourceId && staffList && staffList.length > 0 && (
            <div className="bdp-edit-field">
              <label className="bdp-edit-label">Staff Member</label>
              <StaffPicker
                label="Staff Member"
                staffList={staffList}
                value={staffId}
                onChange={(id) => this.setState({ staffId: id })}
              />
            </div>
          )}

          {/* Leave Type (leave only) — categorises for reporting later.
              "Other" is the escape hatch for things like hospital
              appointments or personal leave that don't fit holiday/sick. */}
          {isLeave && (
            <div className="bdp-edit-field">
              <label className="bdp-edit-label">Leave Type</label>
              <div className="nep-leave-type">
                <button
                  type="button"
                  className={`nep-leave-type-btn ${this.state.leaveType === 'holiday' ? 'nep-leave-type-btn--selected' : ''}`}
                  onClick={() => this.setState({ leaveType: 'holiday' })}
                >
                  <span className="nep-leave-type-icon" role="img" aria-label="holiday">🌴</span>
                  Holiday
                </button>
                <button
                  type="button"
                  className={`nep-leave-type-btn ${this.state.leaveType === 'sick' ? 'nep-leave-type-btn--selected' : ''}`}
                  onClick={() => this.setState({ leaveType: 'sick' })}
                >
                  <span className="nep-leave-type-icon" role="img" aria-label="sick">🤒</span>
                  Sick Day
                </button>
                <button
                  type="button"
                  className={`nep-leave-type-btn ${this.state.leaveType === 'other' ? 'nep-leave-type-btn--selected' : ''}`}
                  onClick={() => this.setState({ leaveType: 'other' })}
                >
                  <span className="nep-leave-type-icon" role="img" aria-label="other">📋</span>
                  Other
                </button>
              </div>
            </div>
          )}

          {/* Title */}
          <div className="bdp-edit-field">
            <label className="bdp-edit-label">{isLeave ? 'Leave Title (optional)' : 'Event Title'}</label>
            <input
              type="text"
              className="bdp-edit-input"
              style={{ textAlign: 'left' }}
              placeholder={isLeave ? 'e.g. Annual leave, Training...' : 'e.g. Team meeting, Lunch break...'}
              value={title}
              onChange={(e) => {
                const newTitle = e.target.value;
                const suggested = matchEventEmoji(newTitle);
                this.setState({ title: newTitle, ...(suggested && !this.state.emoji ? { emoji: suggested } : {}) });
              }}
              maxLength={200}
            />
          </div>

          {/* Date */}
          <div className="bdp-edit-field">
            <label className="bdp-edit-label">{isLeave ? 'Start Date' : 'Date'}</label>
            <div
              className="bdp-edit-input"
              ref={el => { this._dateAnchor = el; }}
              onClick={() => this.setState({ datePickerOpen: true })}
            >
              {startDate ? moment(startDate).format('ddd D MMM YYYY') : <span style={{ color: '#999' }}>Select date</span>}
            </div>
            <DiaryDatePicker
              open={datePickerOpen}
              selected={startDate || new Date()}
              anchorEl={this._dateAnchor}
              onChange={(date) => {
                const d = moment(date).format('YYYY-MM-DD');
                this.setState({ startDate: d, datePickerOpen: false });
                if (isLeave && moment(d).isAfter(endDate)) {
                  this.setState({ endDate: d });
                }
              }}
              onClose={() => this.setState({ datePickerOpen: false })}
            />
          </div>

          {/* End Date (leave only) */}
          {isLeave && (
            <div className="bdp-edit-field">
              <label className="bdp-edit-label">End Date</label>
              <div
                className="bdp-edit-input"
                ref={el => { this._endDateAnchor = el; }}
                onClick={() => this.setState({ endDatePickerOpen: true })}
              >
                {endDate ? moment(endDate).format('ddd D MMM YYYY') : <span style={{ color: '#999' }}>Select date</span>}
              </div>
              <DiaryDatePicker
                open={endDatePickerOpen}
                selected={endDate || new Date()}
                anchorEl={this._endDateAnchor}
                onChange={(date) => {
                  this.setState({ endDate: moment(date).format('YYYY-MM-DD'), endDatePickerOpen: false });
                }}
                onClose={() => this.setState({ endDatePickerOpen: false })}
              />
            </div>
          )}

          {/* Start / End Time (event only) */}
          {!isLeave && (
            <div className="bdp-edit-row">
              <div className="bdp-edit-field bdp-edit-field--half">
                <label className="bdp-edit-label">Start Time</label>
                <ShiftTimePicker
                  label="Start Time"
                  placeholder="Select time"
                  selected={startMinutes != null ? (() => { const d = new Date(); d.setHours(Math.floor(startMinutes / 60), startMinutes % 60, 0, 0); return d; })() : null}
                  onChange={(date) => this.setState({ startMinutes: date.getHours() * 60 + date.getMinutes() })}
                />
              </div>
              <div className="bdp-edit-field bdp-edit-field--half">
                <label className="bdp-edit-label">Finish Time</label>
                <ShiftTimePicker
                  label="Finish Time"
                  placeholder="Select time"
                  selected={endMinutes != null ? (() => { const d = new Date(); d.setHours(Math.floor(endMinutes / 60), endMinutes % 60, 0, 0); return d; })() : null}
                  onChange={(date) => this.setState({ endMinutes: date.getHours() * 60 + date.getMinutes() })}
                />
              </div>
            </div>
          )}

          {/* The bookings it lands on, before saving */}
          <div ref={(el) => { this._affectedRef = el; }}>
            <AffectedBookings
              list={this.state.affected}
              until={this.state.affectedUntil}
              onMove={this.props.onOpenBooking ? (b) => this.props.onOpenBooking(b, 'edit') : null}
              onMessage={this.props.onOpenBooking ? (b) => this.props.onOpenBooking(b, 'message') : null}
            />
          </div>

          {/* Colour picker */}
          <div className="bdp-edit-field">
            <label className="bdp-edit-label">Colour</label>
            <div className="nep-colour-picker">
              {COLOUR_PRESETS.map(c => (
                <button
                  key={c}
                  type="button"
                  className={`nep-colour-swatch ${this.state.colour === c ? 'nep-colour-swatch--selected' : ''}`}
                  style={{ background: c }}
                  onClick={() => this.setState({ colour: c })}
                >
                  {this.state.colour === c && (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2.5 6L5 8.5L9.5 3.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Emoji */}
          <div className="bdp-edit-field">
            <label className="bdp-edit-label">Emoji</label>
            {this.state.emoji ? (
              <div className="nep-emoji-selected">
                <span className="nep-emoji-selected-icon">{this.state.emoji}</span>
                <span className="nep-emoji-selected-text">Shows on the diary block</span>
                <button type="button" className="nep-emoji-clear" onClick={() => this.setState({ emoji: '' })}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M3 3L9 9M9 3L3 9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                  </svg>
                </button>
              </div>
            ) : (
              <div className="nep-emoji-empty">
                <span className="nep-emoji-empty-text">Type a title to get a suggestion, or pick one</span>
              </div>
            )}
            <div className="nep-emoji-suggestions">
              {['✈️', '🏖️', '🌴', '⚽', '💪', '🛍️', '🦷', '🩺', '📚', '👥', '🍽️', '☕', '🎂', '🎉', '📸', '💇', '🏠', '💼', '✨', '🎄'].map(e => (
                <button key={e} type="button" className={`nep-emoji-btn ${this.state.emoji === e ? 'nep-emoji-btn--selected' : ''}`}
                  onClick={() => this.setState({ emoji: this.state.emoji === e ? '' : e })}>
                  {e}
                </button>
              ))}
            </div>
          </div>

          {/* Still available for booking (event only) */}
          {!isLeave && (
            <div className="bdp-section" style={{ borderTop: 'none', marginTop: 8, paddingTop: 0 }}>
              <label className="nep-checkbox-row" onClick={() => this.setState(prev => ({ isBookingAllowed: !prev.isBookingAllowed }))}>
                <span className={`nep-checkbox ${isBookingAllowed ? 'nep-checkbox--checked' : ''}`}>
                  {isBookingAllowed && (
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2.5 6L5 8.5L9.5 3.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </span>
                <span className="nep-checkbox-label">Still available for booking</span>
              </label>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bdp-footer">
          <button className="bdp-btn bdp-btn--secondary" onClick={onClose}>Cancel</button>
          <button className="bdp-btn bdp-btn--primary" onClick={this.handleSaveClick} disabled={saving}>
            {saving ? (this.state.affectedChecking ? 'Checking...' : 'Saving...') : this.state.affected && this.state.affected.length ? 'Save anyway' : isLeave ? 'Add Leave' : 'Add Event'}
          </button>
        </div>
      </SlidePanel>
    );
  }
}

export default NewEventPanel;
